// image_association.ts
import { Quat, Vec3, Mat4 } from 'playcanvas'; // Mat4 should already be here
import { Events } from './events';
import { localize } from './ui/localization';

/** Supersplat's global 180° Z flip. */
const RZ_PI = new Quat().setFromEulerAngles(0, 0, 180);

/** Angular distance (radians) between quaternions, using |dot| to handle q and -q. */
function quatAngularDistanceRad(a: Quat, b: Quat): number {
    const qa = a.clone().normalize();
    const qb = b.clone().normalize();
    const dot = Math.abs(qa.dot(qb));
    const clamped = Math.min(1, Math.max(-1, dot));
    return 2 * Math.acos(clamped);
}

/** Angle (radians) between two world-space direction vectors. */
function vecAngleRad(a: Vec3, b: Vec3): number {
    const na = a.clone().normalize();
    const nb = b.clone().normalize();
    const dot = Math.min(1, Math.max(-1, na.dot(nb)));
    return Math.acos(dot);
}

/** Front half-space test (≤ 90° from forward). */
function isInFront(camPos: Vec3, camForward: Vec3, posePos: Vec3): boolean {
    const dir = posePos.clone().sub(camPos);
    const lenSq = dir.lengthSq();
    if (lenSq === 0) return true;
    dir.mulScalar(1 / Math.sqrt(lenSq));
    return camForward.dot(dir) >= 0;
}

/** Compute camera forward from whatever we have. */
function computeForwardFromAny(cam: { forward?: Vec3; quat?: Quat; euler?: Vec3 }): Vec3 | null {
    if (cam.forward && cam.forward.lengthSq() > 0) return cam.forward.clone().normalize();
    const basisF = new Vec3(0, 0, -1); // PlayCanvas local forward
    if (cam.quat) return cam.quat.clone().transformVector(basisF).normalize();
    if (cam.euler) {
        const q = new Quat().setFromEulerAngles(cam.euler.x, cam.euler.y, cam.euler.z);
        return q.transformVector(basisF).normalize();
    }
    return null;
}

/** Apply Supersplat flip to rotation, with controllable multiply order. */
function applyFlipToRot(rot: Quat, flip: boolean, order: 'pre' | 'post'): Quat {
    if (!flip) return rot;
    return order === 'pre' ? RZ_PI.clone().mul(rot) : rot.clone().mul(RZ_PI);
}

/** Apply Supersplat flip to position. (global coord flip) */
function applyFlipToPos(pos: Vec3, flip: boolean): Vec3 {
    return flip ? RZ_PI.transformVector(pos.clone()) : pos;
}

// A temporary Mat4 to avoid creating new matrices every time in bestPoseForward
const tempMat4 = new Mat4();
// Constants for setTRS
const ZERO_VEC3 = new Vec3(0, 0, 0);
const ONE_VEC3 = new Vec3(1, 1, 1);


/** Get a pose's world forward vector from its rotation. Prefers actual -Z direction but allows fallback. */
function bestPoseForward(
    poseRot: Quat,
    camForward: Vec3
): { forward: Vec3; angleRad: number; usedBasis: -1 | 1 } {
    // Create a matrix representing only the rotation (and reflection) from the quaternion
    tempMat4.setTRS(ZERO_VEC3, poseRot, ONE_VEC3);

    // Extract the transformed Z-axis directly from the matrix.
    const zAxis = tempMat4.getZ();

    // Try both directions but prefer the actual forward (-Z)
    const fNegZ = zAxis.clone().mulScalar(-1);
    const fPosZ = zAxis.clone();

    const aNeg = vecAngleRad(fNegZ, camForward);
    const aPos = vecAngleRad(fPosZ, camForward);

    // Prefer actual forward (-Z) if angle is reasonable (< 90°)
    // This prevents rear-facing images from being selected when forward-facing options exist
    if (aNeg < Math.PI / 2) {
        return { forward: fNegZ, angleRad: aNeg, usedBasis: -1 };
    } else if (aPos < aNeg) {
        // Fall back to flipped direction only if actual forward is rear-facing
        return { forward: fPosZ, angleRad: aPos, usedBasis: 1 };
    } else {
        return { forward: fNegZ, angleRad: aNeg, usedBasis: -1 };
    }
}


type ClosestOptions = {
    /** Require pose forward to be within this angle (degrees) of camera forward. Default 30. */
    maxForwardAngleDeg?: number;
    /** Enforce front half-space (dot ≥ 0). Default true. */
    strictFront?: boolean;
    /** Consider Supersplat +Z 180° flip. Default true. */
    flipZ180?: boolean;
    /** Flip multiply order: 'pre' or 'post'. Default 'pre'. (We auto-try both anyway.) */
    flipOrder?: 'pre' | 'post';
    /** Use rotation as a secondary tiebreak when distances are ~equal. Default true. */
    useRotationTieBreak?: boolean;
    /** Angle tiebreak weight (scene units per rad). Default 0.02. */
    angleWeight?: number;
    /** Popups on errors. */
    withPopups?: boolean;
    /** Console debug. */
    debug?: boolean;
};

const DEFAULTS = {
    maxForwardAngleDeg: 30,
    strictFront: true,
    flipZ180: true,
    flipOrder: 'pre' as const,
    useRotationTieBreak: true,
    angleWeight: 0.02
};

const registerImageAssociationEvents = (events: Events) => {
    function findBest(opts?: ClosestOptions, infoOnly = false) {
        const maxForwardAngleDeg = opts?.maxForwardAngleDeg ?? DEFAULTS.maxForwardAngleDeg;
        const strictFront = opts?.strictFront ?? DEFAULTS.strictFront;
        const flipZ180 = opts?.flipZ180 ?? DEFAULTS.flipZ180;
        const flipOrderDefault = opts?.flipOrder ?? DEFAULTS.flipOrder;
        const useRotTie = opts?.useRotationTieBreak ?? DEFAULTS.useRotationTieBreak;
        const angleWeight = opts?.angleWeight ?? DEFAULTS.angleWeight;
        const withPopups = !!opts?.withPopups;
        const debug = !!opts?.debug;

        // Camera
        const cam = events.invoke('camera.getTransform') as
            | { position: Vec3; quat?: Quat; euler?: Vec3; forward?: Vec3 }
            | null;
        if (!cam) {
            if (withPopups && !infoOnly) {
                events.invoke('showPopup', {
                    type: 'error',
                    header: localize('assoc.error') ?? 'Association Error',
                    message: localize('assoc.noCamera') ?? 'Camera transform is not available.'
                });
            }
            return null;
        }
        const camPos = cam.position;
        const camRot = cam.quat ?? new Quat();
        const camFwd = computeForwardFromAny(cam);
        if (!camFwd) {
            if (withPopups && !infoOnly) {
                events.invoke('showPopup', {
                    type: 'error',
                    header: localize('assoc.error') ?? 'Association Error',
                    message: 'Cannot compute camera forward (need forward, quat, or euler).'
                });
            }
            return null;
        }

        // Poses - IMPORTANT: Get the live, transformed list
        const poses = events.invoke('poses.list') as
            | Array<{ name: string; position: Vec3; rotation: Quat }>
            | null
            | undefined;
        if (!poses?.length) {
            if (withPopups && !infoOnly) {
                events.invoke('showPopup', {
                    type: 'error',
                    header: localize('assoc.error') ?? 'Association Error',
                    message: localize('assoc.noPoses') ?? 'No pose JSON loaded.'
                });
            }
            return null;
        }

        // Images
        const tuples = events.invoke('images.all') as Array<[string, File]> | null | undefined;
        if (!tuples?.length) {
            if (withPopups && !infoOnly) {
                events.invoke('showPopup', {
                    type: 'error',
                    header: localize('assoc.error') ?? 'Association Error',
                    message: localize('assoc.noImages') ?? 'No images imported.'
                });
            }
            return null;
        }
        const toKey = (s: string) => s.toLowerCase();
        const imageMap = new Map<string, File>(tuples.map(([n, f]) => [toKey(n), f]));
        const named = poses.filter(p => imageMap.has(toKey(p.name)));
        if (!named.length) {
            if (withPopups && !infoOnly) {
                events.invoke('showPopup', {
                    type: 'error',
                    header: localize('assoc.error') ?? 'Association Error',
                    message: localize('assoc.noMatches') ?? 'No pose names match imported image filenames.'
                });
            }
            return null;
        }

        const maxForwardAngleRad = (maxForwardAngleDeg * Math.PI) / 180;

        type Cand = {
            name: string;
            posePos: Vec3;
            poseRot: Quat;
            dist: number;
            front: boolean;
            fwdAngleRad: number;   // angle between pose forward and cam forward (smallest across basis & flip-order)
            usedBasis: -1 | 1;
        };

        const candidates: Cand[] = [];

        for (const p of named) {
            const fwd = bestPoseForward(p.rotation, camFwd);
            const dist = camPos.distance(p.position);
            const front = isInFront(camPos, camFwd, p.position);

            candidates.push({
                name: p.name,
                poseRot: p.rotation,
                posePos: p.position,
                dist,
                front,
                fwdAngleRad: fwd.angleRad,
                usedBasis: fwd.usedBasis
            });
        }

        // Filter by front half-space (if strict) and by forward-angle threshold (hard gate).
        let pool = candidates.filter(c => c.fwdAngleRad <= maxForwardAngleRad);
        if (strictFront) {
            pool = pool.filter(c => c.front);
        } else if (!pool.length) {
            pool = candidates.filter(c => c.fwdAngleRad <= maxForwardAngleRad);
        }

        if (debug) {
            // eslint-disable-next-line no-console
            console.debug(`[assoc] named=${named.length} cand=${candidates.length} pool=${pool.length}`);
            const peek = (arr: Cand[], tag: string) => {
                console.debug(`[assoc] ${tag} peek:`,
                    arr.slice(0, 5).map(c => ({
                        name: c.name,
                        dist: c.dist.toFixed(3),
                        fwdDeg: (c.fwdAngleRad * 180 / Math.PI).toFixed(1),
                        front: c.front,
                        basis: c.usedBasis
                    }))
                );
            };
            peek(pool, 'pool');
            if (!pool.length) {
                const sortByAngle = [...candidates].sort((a, b) => a.fwdAngleRad - b.fwdAngleRad);
                peek(sortByAngle, 'closest-by-angle (no gate)');
            }
        }

        if (!pool.length) {
            if (withPopups && !infoOnly) {
                events.invoke('showPopup', {
                    type: 'error',
                    header: localize('assoc.error') ?? 'Association Error',
                    message: `No pose aligns within ${maxForwardAngleDeg}° of the camera forward${strictFront ? ' and in front-half space' : ''}.`
                });
            }
            return null;
        }

        // Select by NEAREST DISTANCE; tiebreak by smaller forward-angle (and optionally angular quat difference).
        let best = pool[0];
        for (let i = 1; i < pool.length; i++) {
            const c = pool[i];
            if (c.dist < best.dist - 1e-6) {
                best = c;
            } else if (Math.abs(c.dist - best.dist) <= 1e-6) {
                if (c.fwdAngleRad < best.fwdAngleRad - 1e-6) {
                    best = c;
                } else if (useRotTie && Math.abs(c.fwdAngleRad - best.fwdAngleRad) <= 1e-6) {
                    const bestAng = quatAngularDistanceRad(camRot, best.poseRot);
                    const cAng = quatAngularDistanceRad(camRot, c.poseRot);
                    const bestScore = angleWeight * bestAng;
                    const cScore = angleWeight * cAng;
                    if (cScore < bestScore) best = c;
                }
            }
        }

        const file = imageMap.get(toKey(best.name)) || null;
        if (infoOnly) {
            return {
                name: best.name,
                score: best.dist + (useRotTie ? angleWeight * quatAngularDistanceRad(camRot, best.poseRot) : 0),
                angleDeg: (best.fwdAngleRad * 180 / Math.PI),
                posDistance: best.dist,
                front: best.front,
                usedBasis: best.usedBasis,
                file
            };
        }

        if (!file) {
            if (withPopups) {
                events.invoke('showPopup', {
                    type: 'error',
                    header: localize('assoc.error') ?? 'Association Error',
                    message: `Image '${best.name}' not found in imported images.`
                });
            }
            return null;
        }

        events.fire('images.closestToCamera.found', {
            name: best.name,
            score: best.dist,
            angleDeg: (best.fwdAngleRad * 180 / Math.PI),
            posDistance: best.dist,
            front: best.front,
            usedBasis: best.usedBasis,
            file
        });

        return file;
    }

    // Public APIs
    events.function('images.closestToCamera', (opts?: ClosestOptions) => findBest(opts, false));
    events.function('images.closestToCamera.info', (opts?: ClosestOptions) => findBest(opts, true));
};

export { registerImageAssociationEvents };

